import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../assets/logo.png';
import { 
  LayoutDashboard, 
  FileText, 
  Plus, 
  Users, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  User,
  Shield,
  Crown,
  BarChart3,
  Eye,
  UserPlus,
  Lock,
  FolderOpen
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import NotificationDropdown from './NotificationDropdown';
import GlobalSearch from './GlobalSearch';

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout, isSuperAdmin, isAdmin } = useAuth();
  const roleValue = typeof user?.role === 'object' ? user?.role?.name : user?.role;
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigation = [
    { name: 'Super Admin Dashboard', href: '/super-admin', icon: LayoutDashboard, roles: ['SUPER_ADMIN'] },
    { name: 'Employee List', href: '/super-admin/employee-list', icon: Users, roles: ['SUPER_ADMIN'] },
    { name: 'Admin Panel', href: '/admin', icon: Shield, roles: ['ADMIN'] },
    { name: 'Employee List', href: '/admin/employee-list', icon: Users, roles: ['ADMIN'] },
    { name: 'Manager Dashboard', href: '/manager', icon: LayoutDashboard, roles: ['MANAGER'] },
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, roles: ['EMPLOYEE'] },
    { name: 'My Projects', href: '/projects', icon: FolderOpen, roles: ['EMPLOYEE'] },
    { name: 'My Timesheets', href: '/timesheets', icon: FileText, roles: ['EMPLOYEE', 'MANAGER'] },
    { name: 'Submit Timesheet', href: '/timesheets/create', icon: Plus, roles: ['EMPLOYEE', 'MANAGER'] },
    { name: 'Employee Management', href: '/manager/employees', icon: UserPlus, roles: ['MANAGER'] },
    { name: 'Timesheet Management', href: '/manager/timesheets', icon: Eye, roles: ['MANAGER'] },
    { name: 'Project Management', href: '/manager/projects', icon: FileText, roles: ['MANAGER'] },
    { name: 'Consolidation', href: '/admin/consolidated-export', icon: FileText, roles: ['ADMIN'] },
    { name: 'Consolidation', href: '/super-admin/consolidated-export', icon: FileText, roles: ['SUPER_ADMIN'] },
    { name: 'Timesheet Review', href: '/admin/timesheets/review', icon: Eye, roles: ['ADMIN', 'SUPER_ADMIN'] },
    { name: 'Draft Report', href: '/admin/draft-report', icon: BarChart3, roles: ['ADMIN', 'SUPER_ADMIN'] },
    { name: 'User Management', href: '/super-admin/users', icon: Users, roles: ['SUPER_ADMIN'] },
    { name: 'Employee Management', href: '/admin/employees', icon: UserPlus, roles: ['ADMIN'] },
    { name: 'Project Management', href: '/admin/projects', icon: FileText, roles: ['ADMIN', 'SUPER_ADMIN'] },
    { name: 'Password Management', href: '/admin/passwords', icon: Lock, roles: ['ADMIN'] },
    { name: 'Password Management', href: '/super-admin/passwords', icon: Lock, roles: ['SUPER_ADMIN'] },
    { name: 'Settings', href: '/super-admin/settings', icon: Settings, roles: ['SUPER_ADMIN'] },
    { name: 'Profile', href: '/profile', icon: User, roles: ['EMPLOYEE', 'ADMIN', 'SUPER_ADMIN', 'MANAGER'] },
  ];

  const filteredNavigation = navigation.filter(item => 
    item.roles.includes(roleValue)
  );

  const getRoleIcon = (role) => {
    switch (role) {
      case 'SUPER_ADMIN':
        return <Crown className="w-4 h-4 text-yellow-500" />;
      case 'ADMIN':
        return <Shield className="w-4 h-4 text-blue-500" />;
      case 'MANAGER':
        return <Users className="w-4 h-4 text-purple-500" />;
      case 'EMPLOYEE':
        return <User className="w-4 h-4 text-green-500" />;
      default:
        return <User className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 lg:hidden"
          >
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
            <motion.div
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              className="relative flex-1 flex flex-col max-w-xs w-full bg-white"
            >
              <div className="absolute top-0 right-0 -mr-12 pt-2">
                <button
                  type="button"
                  className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                  onClick={() => setSidebarOpen(false)}
                >
                  <X className="h-6 w-6 text-white" />
                </button>
              </div>
              <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
                <div className="flex-shrink-0 flex items-center px-4">
                  <img src={logo} alt="Logo" className="h-8 w-auto" />
                </div>
                <nav className="mt-5 px-2 space-y-1">
                  {filteredNavigation.map((item, index) => {
                    const isActive = location.pathname === item.href;
                    const isSuperAdminDashboard = item.name === 'Super Admin Dashboard';
                    const isAdminPanel = item.name === 'Admin Panel';
                    return (
                      <div key={item.name}>
                        <button
                          onClick={() => {
                            navigate(item.href);
                            setSidebarOpen(false);
                          }}
                          className={`${
                            isActive
                              ? isSuperAdminDashboard
                                ? 'bg-gradient-to-r from-yellow-100 to-orange-100 text-yellow-900 border-l-4 border-yellow-500'
                                : isAdminPanel
                                ? 'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-900 border-l-4 border-blue-500'
                                : 'bg-primary-100 text-primary-900'
                              : isSuperAdminDashboard
                                ? 'text-yellow-700 hover:bg-gradient-to-r hover:from-yellow-50 hover:to-orange-50 hover:text-yellow-900'
                                : isAdminPanel
                                ? 'text-blue-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 hover:text-blue-900'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          } group flex items-center px-2 py-2 text-base font-medium rounded-md w-full text-left transition-all duration-200`}
                        >
                          <item.icon className={`mr-4 h-6 w-6 ${isSuperAdminDashboard ? 'text-yellow-600' : isAdminPanel ? 'text-blue-600' : ''}`} />
                          <span className="flex-1">{item.name}</span>
                          {isSuperAdminDashboard && (
                            <span className="ml-2 px-2 py-1 text-xs font-medium bg-yellow-200 text-yellow-800 rounded-full">
                              Dashboard
                            </span>
                          )}
                          {isAdminPanel && (
                            <span className="ml-2 px-2 py-1 text-xs font-medium bg-blue-200 text-blue-800 rounded-full">
                              Panel
                            </span>
                          )}
                        </button>
                        {(isSuperAdminDashboard || isAdminPanel) && (
                          <div className="mx-2 my-2 border-t border-gray-200"></div>
                        )}
                      </div>
                    );
                  })}
                </nav>
              </div>
              {/* Mobile user footer */}
              <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
                <div className="flex items-center w-full">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full bg-primary-500 flex items-center justify-center">
                      <span className="text-sm font-medium text-white">
                        {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                      </span>
                    </div>
                  </div>
                  <div className="ml-3 flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <div className="flex items-center">
                      {getRoleIcon(roleValue)}
                      <span className="ml-1 text-xs text-gray-500">{roleValue}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSidebarOpen(false);
                      handleLogout();
                    }}
                    className="ml-2 inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors"
                    aria-label="Logout"
                  >
                    <LogOut className="h-5 w-5" />
                    <span className="hidden sm:inline">Logout</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0">
        <div className="flex-1 flex flex-col min-h-0 bg-white border-r border-gray-200">
          <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
            <div className="flex items-center flex-shrink-0 px-4">
              <img src={logo} alt="Logo" className="h-8 w-auto" />
            </div>
            <nav className="mt-5 flex-1 px-2 space-y-1">
              {filteredNavigation.map((item, index) => {
                const isActive = location.pathname === item.href;
                const isSuperAdminDashboard = item.name === 'Super Admin Dashboard';
                const isAdminPanel = item.name === 'Admin Panel';
                return (
                  <div key={item.name}>
                    <button
                      onClick={() => navigate(item.href)}
                      className={`${
                        isActive
                          ? isSuperAdminDashboard
                            ? 'bg-gradient-to-r from-yellow-100 to-orange-100 text-yellow-900 border-l-4 border-yellow-500'
                            : isAdminPanel
                            ? 'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-900 border-l-4 border-blue-500'
                            : 'bg-primary-100 text-primary-900'
                          : isSuperAdminDashboard
                            ? 'text-yellow-700 hover:bg-gradient-to-r hover:from-yellow-50 hover:to-orange-50 hover:text-yellow-900'
                            : isAdminPanel
                            ? 'text-blue-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 hover:text-blue-900'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      } group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full text-left transition-all duration-200`}
                    >
                      <item.icon className={`mr-3 h-5 w-5 ${isSuperAdminDashboard ? 'text-yellow-600' : isAdminPanel ? 'text-blue-600' : ''}`} />
                      <span className="flex-1">{item.name}</span>
                      {isSuperAdminDashboard && (
                        <span className="ml-2 px-2 py-1 text-xs font-medium bg-yellow-200 text-yellow-800 rounded-full">
                          Dashboard
                        </span>
                      )}
                      {isAdminPanel && (
                        <span className="ml-2 px-2 py-1 text-xs font-medium bg-blue-200 text-blue-800 rounded-full">
                          Panel
                        </span>
                      )}
                    </button>
                    {(isSuperAdminDashboard || isAdminPanel) && (
                      <div className="mx-2 my-2 border-t border-gray-200"></div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
          <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 rounded-full bg-primary-500 flex items-center justify-center">
                  <span className="text-sm font-medium text-white">
                    {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                  </span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                  {user?.firstName} {user?.lastName}
                </p>
                <div className="flex items-center">
                  {getRoleIcon(roleValue)}
                  <span className="ml-1 text-xs text-gray-500">{roleValue}</span>
                </div>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="ml-auto p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col flex-1">
        <div className="sticky top-0 z-10 lg:hidden pl-1 pt-1 sm:pl-3 sm:pt-3 bg-gray-50">
          <button
            type="button"
            className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
        {/* Header with notifications */}
        {['ADMIN', 'SUPER_ADMIN', 'MANAGER', 'EMPLOYEE'].includes(roleValue) && (
          <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              <div className="flex justify-between items-center h-16">
                <div className="hidden md:flex items-center">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {roleValue === 'SUPER_ADMIN' ? 'Super Admin Dashboard' :
                     roleValue === 'ADMIN' ? 'Admin Panel' :
                     roleValue === 'MANAGER' ? 'Manager Dashboard' : 
                     roleValue === 'EMPLOYEE' ? 'Dashboard' : 'Dashboard'}
                  </h2>
                </div>
                <div className="flex items-center space-x-4 md:ml-0 ml-auto">
                  {(roleValue === 'SUPER_ADMIN' || roleValue === 'ADMIN' || roleValue === 'MANAGER') && (
                    <GlobalSearch />
                  )}
                  <NotificationDropdown />
                </div>
              </div>
            </div>
          </div>
        )}

        <main className="flex-1">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Outlet />
              </motion.div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;

